import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { Alert, AlertSeverity, AlertStatus, AlertOwner } from '@cbm/shared';

interface AlertSubscriptionOptions {
  maxAlerts?: number;
  severityFilter?: AlertSeverity[];
  statusFilter?: AlertStatus[];
  ownerFilter?: AlertOwner[];
  machineFilter?: string[];
  onNewAlert?: (alert: Alert) => void;
  onAlertUpdated?: (alert: Alert) => void;
  onError?: (error: Error) => void;
}

interface AlertState {
  alerts: Alert[];
  newAlerts: Alert[];
  updatedAlerts: Alert[];
  isLoading: boolean;
  error: string | null;
  lastUpdate: string | null;
}

export function useAlertSubscription(options: AlertSubscriptionOptions = {}) {
  const {
    maxAlerts = 500,
    severityFilter,
    statusFilter,
    ownerFilter,
    machineFilter,
    onNewAlert,
    onAlertUpdated,
    onError,
  } = options;

  const { lastMessage, isConnected } = useWebSocket();
  const [state, setState] = useState<AlertState>({
    alerts: [],
    newAlerts: [],
    updatedAlerts: [],
    isLoading: false,
    error: null,
    lastUpdate: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // Check if alert passes filters
  const passesFilters = useCallback((alert: Alert) => {
    if (severityFilter && !severityFilter.includes(alert.severity)) return false;
    if (statusFilter && !statusFilter.includes(alert.status)) return false;
    if (ownerFilter && !ownerFilter.includes(alert.owner)) return false;
    if (machineFilter && !machineFilter.includes(alert.machineId)) return false;
    return true;
  }, [severityFilter, statusFilter, ownerFilter, machineFilter]);

  // Fetch existing alerts on mount
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setState(current => ({ ...current, isLoading: true }));
        
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
        
        // Fetch alerts for all machines
        const machines = ['M-001', 'M-002', 'M-003'];
        const allAlerts: Alert[] = [];
        
        for (const machineId of machines) {
          const response = await fetch(`${API_BASE_URL}/machines/${machineId}/alerts`);
          if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data)) {
              allAlerts.push(...data.filter(passesFilters));
            }
          }
        }
        
        console.log('useAlertSubscription: Fetched alerts from API', { count: allAlerts.length });
        
        setState(current => ({
          ...current,
          alerts: allAlerts.slice(0, maxAlerts),
          isLoading: false,
          lastUpdate: new Date().toISOString(),
        }));
      } catch (error) {
        console.error('useAlertSubscription: Error fetching alerts', error);
        setState(current => ({
          ...current,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch alerts',
        }));
      }
    };
    
    fetchAlerts();
  }, [passesFilters, maxAlerts]);

  // Process incoming alert messages
  useEffect(() => {
    if (!lastMessage || !isConnected) return;

    console.log('useAlertSubscription: Received message', { type: lastMessage.type, payload: lastMessage.payload });

    if (lastMessage.type === 'alert_created' || lastMessage.type === 'alert_updated') {
      try {
        const { alert } = lastMessage.payload;
        
        // Validate alert has required fields from DynamoDB
        if (!alert || !alert.alertId || !alert.machineId || !alert.createdAt) {
          console.warn('useAlertSubscription: Invalid alert received, missing required fields', alert);
          return;
        }
        
        const passes = passesFilters(alert);
        
        console.log('useAlertSubscription: Processing alert', { 
          alertId: alert.alertId, 
          type: lastMessage.type,
          passesFilters: passes,
          filters: { severityFilter, statusFilter, ownerFilter, machineFilter }
        });
        
        // Apply filters
        if (!passes) {
          console.log('useAlertSubscription: Alert filtered out', { alertId: alert.alertId });
          return;
        }

        setState(current => {
          const existingIndex = current.alerts.findIndex(a => a.alertId === alert.alertId);
          let updatedAlerts: Alert[];
          let newAlerts = [...current.newAlerts];
          let updatedAlertsList = [...current.updatedAlerts];

          if (existingIndex >= 0) {
            // Update existing alert
            updatedAlerts = [...current.alerts];
            updatedAlerts[existingIndex] = alert;
            updatedAlertsList.push(alert);
            console.log('useAlertSubscription: Updated existing alert', { alertId: alert.alertId });
          } else {
            // Add new alert
            updatedAlerts = [alert, ...current.alerts].slice(0, maxAlerts);
            newAlerts.push(alert);
            console.log('useAlertSubscription: Added new alert', { alertId: alert.alertId, totalAlerts: updatedAlerts.length });
          }

          return {
            ...current,
            alerts: updatedAlerts,
            newAlerts,
            updatedAlerts: updatedAlertsList,
            lastUpdate: new Date().toISOString(),
            error: null,
          };
        });

        // Call callbacks
        if (lastMessage.type === 'alert_created') {
          onNewAlert?.(alert);
        } else {
          onAlertUpdated?.(alert);
        }

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error processing alert';
        console.error('useAlertSubscription: Error processing alert', error);
        setState(current => ({
          ...current,
          error: errorMessage,
        }));
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      }
    }
  }, [lastMessage, isConnected, passesFilters, maxAlerts, onNewAlert, onAlertUpdated, onError]);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setState(current => ({
      ...current,
      newAlerts: [],
      updatedAlerts: [],
    }));
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setState(current => ({
      ...current,
      alerts: [],
      newAlerts: [],
      updatedAlerts: [],
      lastUpdate: null,
      error: null,
    }));
  }, []);

  // Get alerts by machine
  const getAlertsByMachine = useCallback((machineId: string) => {
    return state.alerts.filter(alert => alert.machineId === machineId);
  }, [state.alerts]);

  // Get alerts by severity
  const getAlertsBySeverity = useCallback((severity: AlertSeverity) => {
    return state.alerts.filter(alert => alert.severity === severity);
  }, [state.alerts]);

  // Get alerts by status
  const getAlertsByStatus = useCallback((status: AlertStatus) => {
    return state.alerts.filter(alert => alert.status === status);
  }, [state.alerts]);

  // Get alerts by owner
  const getAlertsByOwner = useCallback((owner: AlertOwner) => {
    return state.alerts.filter(alert => alert.owner === owner);
  }, [state.alerts]);

  // Get alert statistics
  const getAlertStatistics = useCallback(() => {
    const stats = {
      total: state.alerts.length,
      byStatus: {
        OPEN: 0,
        ACKNOWLEDGED: 0,
        CLOSED: 0,
      },
      bySeverity: {
        EARLY: 0,
        MID: 0,
        LATE: 0,
      },
      byOwner: {
        PLANT: 0,
        OEM: 0,
      },
      byMachine: {} as Record<string, number>,
    };

    state.alerts.forEach(alert => {
      stats.byStatus[alert.status]++;
      stats.bySeverity[alert.severity]++;
      stats.byOwner[alert.owner]++;
      stats.byMachine[alert.machineId] = (stats.byMachine[alert.machineId] || 0) + 1;
    });

    return stats;
  }, [state.alerts]);

  // Get recent alerts (last N hours)
  const getRecentAlerts = useCallback((hours: number = 24) => {
    const cutoffTime = new Date(Date.now() - hours * 60 * 60 * 1000);
    return state.alerts.filter(alert => 
      new Date(alert.createdAt) >= cutoffTime
    );
  }, [state.alerts]);

  // Get active alerts (open or acknowledged)
  const getActiveAlerts = useCallback(() => {
    return state.alerts.filter(alert => 
      alert.status === 'OPEN' || alert.status === 'ACKNOWLEDGED'
    );
  }, [state.alerts]);

  // Get escalated alerts (late severity that are still open)
  const getEscalatedAlerts = useCallback(() => {
    return state.alerts.filter(alert => 
      alert.severity === 'LATE' && alert.status !== 'CLOSED'
    );
  }, [state.alerts]);

  // Update connection state
  useEffect(() => {
    if (!isConnected) {
      setState(current => ({
        ...current,
        isLoading: false,
        error: 'Connection lost',
      }));
    } else {
      setState(current => ({
        ...current,
        error: null,
      }));
    }
  }, [isConnected]);

  return {
    // State
    alerts: state.alerts,
    newAlerts: state.newAlerts,
    updatedAlerts: state.updatedAlerts,
    isLoading: state.isLoading,
    error: state.error,
    lastUpdate: state.lastUpdate,
    isConnected,

    // Actions
    clearNotifications,
    clearAlerts,

    // Data access
    getAlertsByMachine,
    getAlertsBySeverity,
    getAlertsByStatus,
    getAlertsByOwner,
    getAlertStatistics,
    getRecentAlerts,
    getActiveAlerts,
    getEscalatedAlerts,
  };
}

// Specialized hook for machine-specific alerts
export function useMachineAlerts(
  machineId: string,
  options: Omit<AlertSubscriptionOptions, 'machineFilter' | 'onNewAlert' | 'onAlertUpdated'> & {
    onNewAlert?: (alert: Alert, machineId: string) => void;
    onAlertUpdated?: (alert: Alert, machineId: string) => void;
  } = {}
) {
  const { onNewAlert, onAlertUpdated, ...alertOptions } = options;

  const subscription = useAlertSubscription({
    ...alertOptions,
    machineFilter: [machineId],
    onNewAlert: onNewAlert ? (alert) => {
      if (alert.machineId === machineId) {
        onNewAlert(alert, machineId);
      }
    } : undefined,
    onAlertUpdated: onAlertUpdated ? (alert) => {
      if (alert.machineId === machineId) {
        onAlertUpdated(alert, machineId);
      }
    } : undefined,
  });

  // Get machine-specific data
  const machineAlerts = subscription.getAlertsByMachine(machineId);
  const newMachineAlerts = subscription.newAlerts.filter(alert => alert.machineId === machineId);
  const updatedMachineAlerts = subscription.updatedAlerts.filter(alert => alert.machineId === machineId);

  return {
    // Machine-specific state
    machineId,
    alerts: machineAlerts,
    newAlerts: newMachineAlerts,
    updatedAlerts: updatedMachineAlerts,
    isLoading: subscription.isLoading,
    error: subscription.error,
    lastUpdate: subscription.lastUpdate,
    isConnected: subscription.isConnected,

    // Actions
    clearNotifications: subscription.clearNotifications,

    // Data access helpers
    getAlertsBySeverity: subscription.getAlertsBySeverity,
    getAlertsByStatus: subscription.getAlertsByStatus,
    getAlertsByOwner: subscription.getAlertsByOwner,
    getActiveAlerts: () => subscription.getActiveAlerts().filter(alert => alert.machineId === machineId),
    getEscalatedAlerts: () => subscription.getEscalatedAlerts().filter(alert => alert.machineId === machineId),
    getRecentAlerts: (hours?: number) => subscription.getRecentAlerts(hours).filter(alert => alert.machineId === machineId),
  };
}

// Hook for alert notifications with sound/visual feedback
export function useAlertNotifications(options: {
  playSound?: boolean;
  severityFilter?: AlertSeverity[];
  onNotification?: (alert: Alert, type: 'new' | 'updated') => void;
} = {}) {
  const { playSound = false, severityFilter, onNotification } = options;
  
  const subscription = useAlertSubscription({
    severityFilter,
    onNewAlert: (alert) => {
      if (playSound) {
        // Play notification sound based on severity
        const audio = new Audio();
        switch (alert.severity) {
          case 'LATE':
            audio.src = '/sounds/alert-critical.mp3';
            break;
          case 'MID':
            audio.src = '/sounds/alert-warning.mp3';
            break;
          case 'EARLY':
            audio.src = '/sounds/alert-info.mp3';
            break;
        }
        audio.play().catch(() => {
          // Ignore audio play errors (user interaction required)
        });
      }

      onNotification?.(alert, 'new');
    },
    onAlertUpdated: (alert) => {
      onNotification?.(alert, 'updated');
    },
  });

  // Get notification counts
  const notificationCounts = {
    new: subscription.newAlerts.length,
    updated: subscription.updatedAlerts.length,
    total: subscription.newAlerts.length + subscription.updatedAlerts.length,
  };

  return {
    ...subscription,
    notificationCounts,
  };
}